import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Twitter, Phone, Mail, MapPin } from "lucide-react";

import Logo from "./Logo";

export default function Footer() {
  const { t } = useTranslation();

  const quickLinks = [
    { href: "/", label: t("footer.links.home") },
    { href: "/about", label: t("footer.links.about") },
    { href: "/how-it-works", label: t("footer.links.howItWorks") },
    { href: "/faq", label: t("footer.links.faq") },
    { href: "/contact", label: t("footer.links.contact") },
  ];

  const treatmentLinks = (t("footer.treatmentsList", { returnObjects: true }) || []);

  return (
    <footer className="bg-[#0B3C5D] text-white">
      <div className="max-w-[1200px] mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/">
              <Logo white className="mb-5" />
            </Link>
            <p className="text-white/70 text-sm leading-relaxed mb-5">
              {t("footer.description")}
            </p>
            <div className="flex gap-3">
              <a
                href="https://x.com/monimekllc"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-twitter"
                aria-label="Follow us on Twitter"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#3FA7D6] transition-colors"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="https://www.tiktok.com/@monimekllc"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-tiktok"
                aria-label="Follow us on TikTok"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#3FA7D6] transition-colors text-xs font-bold"
              >
                TK
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-5 text-sm uppercase tracking-widest">
              {t("footer.quickLinks")}
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-white/65 hover:text-[#3FA7D6] text-sm transition-colors"
                    data-testid={`footer-link-${link.href.replace("/", "") || "home"}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Treatments */}
          <div>
            <h4 className="font-semibold text-white mb-5 text-sm uppercase tracking-widest">
              {t("footer.treatmentsLinks")}
            </h4>
            <ul className="space-y-2.5">
              {treatmentLinks.map((item, i) => (
                <li key={i}>
                  <Link
                    to="/treatments"
                    className="text-white/65 hover:text-[#3FA7D6] text-sm transition-colors"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-5 text-sm uppercase tracking-widest">
              {t("footer.connect")}
            </h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#3FA7D6] mt-0.5 flex-shrink-0" />
                <a href="mailto:info@monimekllc.com" className="text-white/65 hover:text-white text-sm transition-colors">
                  info@monimekllc.com
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#3FA7D6] mt-0.5 flex-shrink-0" />
                <span className="text-white/65 text-sm">Istanbul, Turkey</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/contact"
                data-testid="footer-cta"
                className="inline-block px-5 py-2.5 bg-[#3FA7D6] text-white text-sm font-semibold rounded-full hover:bg-white hover:text-[#0B3C5D] transition-all duration-200"
              >
                {t("nav.getConsultation")}
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-6 border-t border-white/15">
          <p className="text-white/40 text-xs leading-relaxed mb-3">
            {t("footer.disclaimer")}
          </p>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <p className="text-white/50 text-xs">
              &copy; {new Date().getFullYear()} Monimek Medical Consulting & Tour LLC. {t("footer.rights")}
            </p>
            <Link to="/privacy-policy" className="text-white/50 hover:text-white text-xs transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
